import {
  CUSTOMER_PACKAGING_TYPE_CODE,
  EXPRESS_BOX_TYPE_CODE,
  EXPRESS_ENVELOPE_TYPE_CODE,
  EXPRESS_PACK_TYPE_CODE,
  type PackageTypeId,
} from '../../../../pages/package-selection/package-types.config';
import type {
  PackageSelectionConstraints,
  PackageSelectionItem,
} from '../models/package-selection.model';

/**
 * Package type card view model for the Package step UI.
 * Built from Product Master package-selection — not a hard-coded catalog.
 */
export interface PackageTypeUiOption {
  /** Product Master `packageTypeCode`. */
  id: PackageTypeId;
  label: string;
  dimensions?: string;
  idealFor?: readonly string[];
  imageAssetPath: string;
  order: number;
  requiresDimensions: boolean;
  isSinglePiece: boolean;
  /** Optional i18n key for an info tooltip beside the package type label (e.g. Envelope). */
  tooltipKey?: string;
  /**
   * Optional i18n key for the subtitle under the package type label when Product Master
   * `dimensions` is empty (Customer Packaging: "Envelopes, boxes, roll").
   */
  subtitleKey?: string;
  /** PM constraints are the source of truth when they differ from env fallbacks. */
  constraints: PackageSelectionConstraints;
}

/** i18n subtitle key for Customer Packaging (shown under the type name). */
const CUSTOMER_PACKAGING_SUBTITLE_KEY =
  'createShipment.packageStep.types.customerPackaging.subtitle';

/** i18n tooltip key for Customer Packaging. */
const CUSTOMER_PACKAGING_TOOLTIP_KEY = 'createShipment.packageStep.types.customerPackaging.tooltip';

/** i18n tooltip key for Purolator Express Envelope. */
const EXPRESS_ENVELOPE_TOOLTIP_KEY = 'createShipment.packageStep.types.expressEnvelope.tooltip';

/** i18n tooltip key for Purolator Express Pack. */
const EXPRESS_PACK_TOOLTIP_KEY = 'createShipment.packageStep.types.expressPack.tooltip';

/** i18n tooltip key for Purolator Express Box. */
const EXPRESS_BOX_TOOLTIP_KEY = 'createShipment.packageStep.types.expressBox.tooltip';

const PACKAGE_TYPE_TOOLTIP_KEYS: Readonly<Partial<Record<PackageTypeId, string>>> = {
  [CUSTOMER_PACKAGING_TYPE_CODE]: CUSTOMER_PACKAGING_TOOLTIP_KEY,
  [EXPRESS_ENVELOPE_TYPE_CODE]: EXPRESS_ENVELOPE_TOOLTIP_KEY,
  [EXPRESS_PACK_TYPE_CODE]: EXPRESS_PACK_TOOLTIP_KEY,
  [EXPRESS_BOX_TYPE_CODE]: EXPRESS_BOX_TOOLTIP_KEY,
};

const PACKAGE_TYPE_SUBTITLE_KEYS: Readonly<Partial<Record<PackageTypeId, string>>> = {
  [CUSTOMER_PACKAGING_TYPE_CODE]: CUSTOMER_PACKAGING_SUBTITLE_KEY,
};

/**
 * Maps Product Master / ESN image paths to UI assets under `package-step/`.
 * Figma SVGs (OM-648) are CSS-scaled to the card thumbnail size (116×86 desktop).
 */
const PACKAGE_STEP_IMAGE_PATHS: Readonly<Record<string, string>> = {
  'assets/images/customer-package.svg': 'assets/images/package-step/customer-package.svg',
  'assets/images/express-envelope.svg': 'assets/images/package-step/express-envelope.svg',
  'assets/images/express-pack.svg': 'assets/images/package-step/express-pack.svg',
  'assets/images/express-box.svg': 'assets/images/package-step/express-box.svg',
  'assets/images/my-own-package.svg': 'assets/images/package-step/customer-package.svg',
};

/**
 * Maps Product Master package-selection items to Package step UI options.
 * PM constraints win over any later env-var fallbacks when they differ.
 * Package type images live under `assets/images/package-step/` (Figma SVG sources).
 *
 * @param items - Raw catalog from Product Master (or mock).
 * @param locale - Active UI locale for package type display names.
 * @returns Options sorted by `packageId` ascending.
 */
export function mapPackageSelectionToUi(
  items: readonly PackageSelectionItem[],
  locale: 'en' | 'fr',
): PackageTypeUiOption[] {
  return [...items]
    .sort((a, b) => a.packageId - b.packageId)
    .map((item) => {
      const dimensions = item.dimensions.trim();
      const id = item.packageTypeCode as PackageTypeId;
      return {
        id,
        label: locale === 'fr' ? item.packageType_fr : item.packageType_en,
        dimensions: dimensions.length > 0 ? dimensions : undefined,
        idealFor: item.idealFor,
        imageAssetPath: resolvePackageImagePath(item.imagePath),
        order: item.packageId,
        requiresDimensions: id === CUSTOMER_PACKAGING_TYPE_CODE,
        isSinglePiece: item.constraints.quantity.equal === 1,
        tooltipKey: PACKAGE_TYPE_TOOLTIP_KEYS[id],
        subtitleKey: dimensions.length > 0 ? undefined : PACKAGE_TYPE_SUBTITLE_KEYS[id],
        constraints: item.constraints,
      };
    });
}

/**
 * Resolves Product Master or ESN image paths to files under `package-step/`.
 * Unknown paths pass through unchanged.
 */
function resolvePackageImagePath(imagePath: string): string {
  return PACKAGE_STEP_IMAGE_PATHS[imagePath] ?? imagePath;
}
